import { createContext, useState } from 'react';

export type PrefetchType = (_: { queryKey: unknown; queryFn: () => Promise<unknown> }) => unknown;
export type FetchType = <T>(url: string) => Promise<T>;

export type MyEconomyDrawerType = {
    isOpen: boolean;
    startCourse: string;
};

export type SupremeCardApplicationType = {
    selectedCard: string;
    step: 1 | 2 | 3;
    isError: boolean;
    progressText: {
        title: string;
        content: string;
    };
};

export type AppContextType = {
    version: string;
    role: 'draft' | 'live';
    siteId: 'resursbank_se' | 'resursbank_no' | 'resursbank_dk' | 'resursbank_fi';
    country: 'se' | 'no' | 'dk' | 'fi';
    language: 'sv' | 'no' | 'da' | 'fi';
    locale: 'sv-se' | 'nb-no' | 'da-dk' | 'fi-fi';
    timezone: string;
    currency: 'SEK' | 'NOK' | 'DKK' | 'EUR';
    host: string;
    url: string;
    isProd: boolean;
    deviceType: 'mobile' | 'desktop';
    bankApiKey: string;
    resursApiUrl: string;
    signicatClientId: string;
    reCaptchaSiteKey: string;
    userAgent: string;
    onetrustId?: string;
    user?: {
        isAuthenticated: boolean;
        isCustomer: boolean;
        hasCardOfferNew: boolean;
        hasCardOfferRaise: boolean;
        accessToken?: string;
        refreshToken?: string;
        tokenExpires?: number;
        pid?: string | null;
        // Real Swedish personnummer. Equal to `pid` in the legacy Signicat
        // flow; in the new oba-auth flow `pid` is an opaque pseudonymous
        // identifier and `govid` carries the actual personnummer resolved
        // by the bank service via /me.
        govid?: string | null;
        // Normalized applicant profile populated in the oba-auth flow from
        // /me so the corporate-deposits form can prefill without calling
        // address_lookup_service (which the v2 scope doesn't cover). Null
        // in the legacy Signicat flow.
        applicant?: {
            firstName: string | null;
            lastName: string | null;
            email: string | null;
            phoneNumber: string | null;
        } | null;
    };
    prefetch: PrefetchType;
    fetch?: FetchType;
    myEconomyDrawer: MyEconomyDrawerType;
    setMyEconomyDrawer: (args: Partial<MyEconomyDrawerType>) => void;
    supremeCardApplication: SupremeCardApplicationType;
    setSupremeCardApplication: (args: Partial<SupremeCardApplicationType>) => void;
    contactMeForm: {
        isOpen: boolean;
        showContactBanner: boolean;
    };
    globalSearchTerm: string;
    setGlobalSearchTerm: (searchTerm: string) => void;
    setContactMeForm: (args: Partial<{ isOpen: boolean; showContactBanner: boolean }>) => void;
};

const AppContext = createContext<AppContextType>({
    version: 'dev',
    role: 'draft',
    siteId: 'resursbank_se',
    country: 'se',
    language: 'sv',
    locale: 'sv-se',
    timezone: 'Europe/Stockholm',
    currency: 'SEK',
    host: 'resursbank.se',
    url: 'https://www.resursbank.se',
    isProd: true,
    deviceType: 'mobile',
    bankApiKey: '',
    resursApiUrl: '',
    signicatClientId: '',
    reCaptchaSiteKey: '',
    userAgent: '',
    user: {
        isAuthenticated: false,
        isCustomer: false,
        hasCardOfferNew: false,
        hasCardOfferRaise: false,
        pid: null,
        govid: null,
        applicant: null,
    },
    prefetch: _ => _,
    myEconomyDrawer: {
        isOpen: false,
        startCourse: 'start',
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setMyEconomyDrawer: (_args: Partial<MyEconomyDrawerType>) => {
        return;
    },
    globalSearchTerm: '',
    setGlobalSearchTerm: () => {
        return;
    },
    contactMeForm: {
        isOpen: false,
        showContactBanner: true,
    },
    setContactMeForm: () => {
        return;
    },
    supremeCardApplication: {
        selectedCard: '',
        step: 1,
        isError: false,
        progressText: {
            title: '',
            content: '',
        },
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setSupremeCardApplication: (_args: Partial<SupremeCardApplicationType>) => {
        return;
    },
});

const AppContextProvider = ({ children, prefetch = _ => _, ...rest }: React.PropsWithChildren<AppContextType>) => {
    const [myEconomyDrawer, setMyEconomyDrawer] = useState<MyEconomyDrawerType>({
        isOpen: false,
        startCourse: 'start',
    });
    const [contactMeForm, setContactMeForm] = useState<{ isOpen: boolean; showContactBanner: boolean }>({
        isOpen: false,
        showContactBanner: true,
    });

    const [supremeCardState, setSupremeCardState] = useState<SupremeCardApplicationType>({
        selectedCard: '',
        step: 1,
        isError: false,
        progressText: {
            title: '',
            content: '',
        },
    });

    const [globalSearchTerm, setGlobalSearchTerm] = useState<string>('');

    return (
        <AppContext.Provider
            value={{
                ...rest,
                prefetch,
                myEconomyDrawer,
                contactMeForm,
                globalSearchTerm,
                setGlobalSearchTerm,
                supremeCardApplication: supremeCardState,
                setMyEconomyDrawer: args => {
                    setMyEconomyDrawer({ ...myEconomyDrawer, ...args });
                },
                setSupremeCardApplication: args => {
                    setSupremeCardState(prev => ({ ...prev, ...args }));
                },
                setContactMeForm: args => {
                    setContactMeForm({ ...contactMeForm, ...args });
                },
            }}
        >
            {children}
        </AppContext.Provider>
    );
};

export { AppContextProvider, AppContext };
